"use client";

import { LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import type { AppLocale } from "@/features/resume/content/dashboard-copy";
import { purchaseCopy } from "@/features/resume/content/purchase-copy";
import { createStripeCheckout, getStripePrice } from "@/features/resume/services/resume-service";
import { FullAccessDetailsModal } from "./full-access-details-modal";

// The API answers in English; known messages are shown in the visitor's language.
const SERVER_MESSAGES: Record<string, keyof (typeof purchaseCopy)["en"]> = {
  "Create a resume before activating full access.": "noResume",
};

function formatPrice(unitAmount: number, currency: string, locale: AppLocale): string {
  return new Intl.NumberFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(unitAmount / 100);
}

export function PurchaseModal({ tenant, locale, onClose }: { tenant: string; locale: AppLocale; onClose(): void }) {
  const copy = purchaseCopy[locale];
  const [price, setPrice] = useState<string | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getStripePrice().then((response) => {
      if (!active || !response.data) return;
      setPrice(formatPrice(response.data.unit_amount, response.data.currency, locale));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [locale]);

  async function payWithStripe() {
    setCheckoutBusy(true);
    setError(null);
    try {
      const response = await createStripeCheckout(tenant);
      window.location.assign(response.data.url);
    } catch (caught) {
      const known = caught instanceof Error ? SERVER_MESSAGES[caught.message] : undefined;
      setError(known ? copy[known] : caught instanceof Error ? caught.message : copy.unavailable);
      setCheckoutBusy(false);
    }
  }

  return <>
    <div className="fixed inset-0 z-50 grid place-items-center bg-foreground/35 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="purchase-modal-title" className="w-full max-w-md rounded-2xl border bg-card p-5 shadow-xl sm:p-7">
        <header className="flex items-center justify-between gap-4">
          <h2 id="purchase-modal-title" className="text-xl font-semibold">{copy.title}</h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={copy.close}><X /></Button>
        </header>
        <p className="mt-2 text-sm text-muted-foreground">{copy.subtitle}</p>

        {error && <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">{error}</p>}

        <Button type="button" className="mt-5 h-12 w-full" onClick={payWithStripe} disabled={checkoutBusy}>
          {checkoutBusy ? <span key="busy" className="inline-flex items-center gap-1.5"><LoaderCircle className="animate-spin" />{copy.redirecting}</span> : <span key="idle">{`${copy.payWithStripe} — ${price ?? copy.priceFallback} ${copy.taxSuffix}`}</span>}
        </Button>

        <div className="mt-5 border-t pt-4 text-center">
          <button type="button" className="text-sm text-primary underline-offset-4 hover:underline" onClick={() => setDetailsOpen(true)}>
            {copy.whatsIncluded}
          </button>
        </div>
      </section>
    </div>
    {detailsOpen && <FullAccessDetailsModal locale={locale} onClose={() => setDetailsOpen(false)} />}
  </>;
}
